/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useMemo, useRef, useState } from "react"
import { serializeDashboardPdf } from "@/datox/dashboard-pdf"

export function DashboardPdfPreview(props) {
  // Freeze the viewed revision and filters while the export dialog is open.
  const [source] = useState(() => ({ ...props, config: structuredClone(props.config), tables: structuredClone(props.tables) }))
  const labels = source.labels
  const defaults = useMemo(() => ({
    orientation: source.config.canvas?.orientation === "portrait" ? "portrait" : "landscape",
    margin: 0,
    scale: 100,
    scope: "active",
    allRows: false,
    density: "original",
    fontSize: source.config.design?.fontSize || "medium",
    gray: !!source.config.design?.lightGrayBackground,
    accent: source.config.design?.accent || "#2f80ed",
    widgets: {},
  }), [source])
  const [settings, setSettings] = useState(defaults)
  const [tables, setTables] = useState(source.tables)
  const [loadFailed, setLoadFailed] = useState(false)
  const [saving, setSaving] = useState(false)
  const [selectedWidget, setSelectedWidget] = useState("")
  const dialogRef = useRef(null)
  const iframeRef = useRef(null)
  const layoutOverridesRef = useRef({})
  const tabs = useMemo(() => source.config.tabs.filter((tab) => settings.scope === "all" || tab.id === source.activeTabID), [source, settings.scope])
  const widgets = useMemo(() => tabs.flatMap((tab) => (tab.widgets || [])
    .map((widget) => ({ tab, widget, key: JSON.stringify([tab.id, widget.id]) }))), [tabs])
  const activeWidget = widgets.find((item) => item.key === selectedWidget) || widgets[0]
  const widgetSettings = settings.widgets[activeWidget?.tab.id]?.[activeWidget?.widget.id] || {}
  const widgetType = activeWidget?.widget.type
  const compactWidget = activeWidget?.widget.layout.h < 150 || settings.fontSize === "compact"
  const titleOnly = widgetType === "header" || widgetType === "title"
  const tableWidget = widgetType === "table" || widgetType === "freeTable"
  const titleSize = widgetSettings.titleSize ?? (widgetType === "header" ? 34 : widgetType === "title" ? compactWidget ? 20 : 28 : compactWidget ? 13 : settings.fontSize === "large" ? 18 : 16)
  const tableDensity = activeWidget?.widget.style?.density
  const contentSize = widgetSettings.contentSize ?? (tableWidget ? tableDensity === "compact" ? 11 : tableDensity === "large" ? 15 : settings.fontSize === "large" ? 15 : compactWidget ? 11 : 13 : widgetType === "text" ? compactWidget ? 12 : 13 : 12)
  const datasetIDs = useMemo(() => [...new Set(tabs.flatMap((tab) => (tab.widgets || [])
    .filter((widget) => widget.type === "table" || widget.type === "freeTable")
    .map((widget) => widget.data?.dataset_id).filter(Boolean)))], [tabs])
  const missingIDs = datasetIDs.filter((id) => !tables.some((table) => table.datasetID === id))
  const html = useMemo(() => serializeDashboardPdf({
    config: source.config,
    tables,
    activeTabID: source.activeTabID,
    settings: withLayoutOverrides(settings, layoutOverridesRef.current),
    dashboardTitle: source.dashboardTitle,
  }), [settings, source, tables])

  useEffect(() => {
    const dialog = dialogRef.current
    dialog.showModal()
    return () => dialog.close()
  }, [])

  useEffect(() => {
    if (!settings.allRows || !missingIDs.length) return
    let cancelled = false
    setLoadFailed(false)
    Promise.resolve(source.onLoadTables(missingIDs)).then((loaded) => {
      if (missingIDs.some((id) => !loaded.some((table) => table.datasetID === id))) throw new Error(labels.loadFailed)
      if (!cancelled) setTables((current) => [...current, ...loaded.filter((table) => !current.some((item) => item.datasetID === table.datasetID))])
    }).catch(() => {
      if (!cancelled) setLoadFailed(true)
    })
    return () => { cancelled = true }
  }, [settings.allRows, missingIDs.join("\0"), source, labels.loadFailed])

  const update = (patch) => setSettings((current) => ({ ...current, ...patch }))
  const updateWidget = (patch) => {
    if (patch === null && activeWidget) {
      const tabLayouts = { ...(layoutOverridesRef.current[activeWidget.tab.id] || {}) }
      delete tabLayouts[activeWidget.widget.id]
      layoutOverridesRef.current = { ...layoutOverridesRef.current, [activeWidget.tab.id]: tabLayouts }
    }
    setSettings((current) => ({
    ...current,
    widgets: {
      ...current.widgets,
      [activeWidget.tab.id]: {
        ...current.widgets[activeWidget.tab.id],
        [activeWidget.widget.id]: patch === null ? {} : { ...current.widgets[activeWidget.tab.id]?.[activeWidget.widget.id], ...patch },
      },
    },
    }))
  }
  const saveLayoutOverride = (tabID, widgetID, layout) => {
    layoutOverridesRef.current = {
      ...layoutOverridesRef.current,
      [tabID]: { ...layoutOverridesRef.current[tabID], [widgetID]: layout },
    }
  }
  const bindPreviewInteractions = () => {
    const frame = iframeRef.current
    const doc = frame?.contentDocument
    if (!doc || doc.body.dataset.layoutEditor) return
    doc.body.dataset.layoutEditor = "true"
    doc.body.classList.add("pdf-editing")
    doc.addEventListener("pointerdown", (event) => {
      if (event.button !== 0) return
      const widget = event.target.closest(".widget[data-widget-id]")
      const grid = widget?.parentElement
      if (!widget || !grid) return
      event.preventDefault()
      const widgetRect = widget.getBoundingClientRect()
      const gridRect = grid.getBoundingClientRect()
      const resizing = event.clientX >= widgetRect.right - 14 && event.clientY >= widgetRect.bottom - 14
      const start = { x: event.clientX, y: event.clientY, left: widgetRect.left - gridRect.left, top: widgetRect.top - gridRect.top, width: widgetRect.width, height: widgetRect.height }
      widget.setPointerCapture?.(event.pointerId)
      const move = (moveEvent) => {
        const dx = moveEvent.clientX - start.x
        const dy = moveEvent.clientY - start.y
        const width = resizing ? Math.max(80, Math.min(gridRect.width - start.left, start.width + dx)) : start.width
        const height = resizing ? Math.max(48, Math.min(gridRect.height - start.top, start.height + dy)) : start.height
        const left = resizing ? start.left : Math.max(0, Math.min(gridRect.width - width, start.left + dx))
        const top = resizing ? start.top : Math.max(0, Math.min(gridRect.height - height, start.top + dy))
        Object.assign(widget.style, { left: `${left}px`, top: `${top}px`, width: `${width}px`, height: `${height}px` })
      }
      const stop = () => {
        doc.removeEventListener("pointermove", move)
        doc.removeEventListener("pointerup", stop)
        doc.removeEventListener("pointercancel", stop)
        frame.contentWindow?.removeEventListener("blur", stop)
        const rect = widget.getBoundingClientRect()
        const canvas = source.config.canvas || { width: 1123, height: 794 }
        saveLayoutOverride(widget.dataset.tabId, widget.dataset.widgetId, {
          x: Math.round((rect.left - gridRect.left) / gridRect.width * canvas.width),
          y: Math.round((rect.top - gridRect.top) / gridRect.height * canvas.height),
          w: Math.round(rect.width / gridRect.width * canvas.width),
          h: Math.round(rect.height / gridRect.height * canvas.height),
        })
        setSelectedWidget(JSON.stringify([widget.dataset.tabId, widget.dataset.widgetId]))
      }
      doc.addEventListener("pointermove", move)
      doc.addEventListener("pointerup", stop)
      doc.addEventListener("pointercancel", stop)
      frame.contentWindow?.addEventListener("blur", stop, { once: true })
    })
  }
  const save = async () => {
    if (saving || (settings.allRows && (loadFailed || missingIDs.length))) return
    setSaving(true)
    try {
      const name = source.config.title || source.dashboardTitle
      const exportHtml = serializeDashboardPdf({ config: source.config, tables, activeTabID: source.activeTabID, settings: withLayoutOverrides(settings, layoutOverridesRef.current), dashboardTitle: source.dashboardTitle })
      const blob = await source.onExport(exportHtml, name)
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = `${name}.pdf`
      link.click()
      URL.revokeObjectURL(url)
    } catch {
      setLoadFailed(true)
    } finally {
      setSaving(false)
    }
  }

  return (
    <dialog ref={dialogRef} className="datox-react-dashboard__pdf-dialog" aria-labelledby="dashboard-pdf-title" onCancel={(event) => { event.preventDefault(); if (!saving) source.onClose() }}>
      <header className="datox-react-dashboard__pdf-header">
        <h2 id="dashboard-pdf-title">{labels.title}</h2>
        <button className="datox-react-dashboard__button" type="button" disabled={saving} onClick={source.onClose}>{labels.close}</button>
      </header>
      <div className="datox-react-dashboard__pdf-body">
        <aside className="datox-react-dashboard__pdf-controls">
          <fieldset disabled={saving}>
            <label>{labels.orientation}<select value={settings.orientation} onChange={(event) => update({ orientation: event.target.value })}>
              <option value="landscape">{labels.landscape}</option><option value="portrait">{labels.portrait}</option>
            </select></label>
            <label>{labels.scope}<select value={settings.scope} onChange={(event) => update({ scope: event.target.value })}>
              <option value="active">{labels.activeTab}</option><option value="all">{labels.allTabs}</option>
            </select></label>
            <label>{labels.margin}<select value={settings.margin} onChange={(event) => update({ margin: Number(event.target.value) })}>
              {[0, 5, 10, 15, 20].map((value) => <option key={value} value={value}>{labels.millimeters.replace("{value}", value)}</option>)}
            </select></label>
            <label>{labels.scale.replace("{value}", settings.scale)}<input type="range" min="50" max="100" step="5" value={settings.scale} onChange={(event) => update({ scale: Number(event.target.value) })} /></label>
            <label>{labels.textSize}<select value={settings.fontSize} onChange={(event) => update({ fontSize: event.target.value })}>
              <option value="compact">{labels.compact}</option><option value="medium">{labels.normal}</option><option value="large">{labels.large}</option>
            </select></label>
            {activeWidget && <details className="datox-react-dashboard__pdf-widget-controls" open>
              <summary>{labels.widgetAdjustments}</summary>
              <p>{labels.widgetHelp}</p>
              <label>{labels.widgetSelect}<select value={activeWidget.key} onChange={(event) => setSelectedWidget(event.target.value)}>
                {widgets.map(({ key, tab, widget }) => <option key={key} value={key}>{labels.widgetOption.replace("{tab}", tab.name || source.dashboardTitle).replace("{name}", widget.title || labels.widgetSelect)}</option>)}
              </select></label>
              {widgetType === "kpi" && <label>{labels.kpiValueScale.replace("{value}", widgetSettings.valueScale ?? 100)}<input type="range" min="25" max="100" step="5" value={widgetSettings.valueScale ?? 100} onChange={(event) => updateWidget({ valueScale: Number(event.target.value) })} /></label>}
              {widgetType !== "text" && <label>{labels.widgetTitleSize.replace("{value}", titleSize)}<input type="range" min="8" max={titleOnly ? 64 : 32} step="1" value={titleSize} onChange={(event) => updateWidget({ titleSize: Number(event.target.value) })} /></label>}
              {!titleOnly && widgetType !== "kpi" && <label>{labels.widgetContentSize.replace("{value}", contentSize)}<input type="range" min="8" max="32" step="1" value={contentSize} onChange={(event) => updateWidget({ contentSize: Number(event.target.value) })} /></label>}
              <label className="datox-react-dashboard__pdf-check"><input type="checkbox" checked={!!widgetSettings.wrapTitle} onChange={(event) => updateWidget({ wrapTitle: event.target.checked })} />{widgetType === "text" ? labels.widgetWrapText : labels.widgetWrapTitle}</label>
              {tableWidget && <label className="datox-react-dashboard__pdf-check"><input type="checkbox" checked={!!widgetSettings.wrapCells} onChange={(event) => updateWidget({ wrapCells: event.target.checked })} />{labels.widgetWrapCells}</label>}
              <button type="button" className="datox-react-dashboard__button" onClick={() => updateWidget(null)}>{labels.widgetReset}</button>
            </details>}
            <label>{labels.accent}<input type="color" value={settings.accent} onChange={(event) => update({ accent: event.target.value })} /></label>
            <label className="datox-react-dashboard__pdf-check"><input type="checkbox" checked={settings.gray} onChange={(event) => update({ gray: event.target.checked })} />{labels.gray}</label>
            <label className="datox-react-dashboard__pdf-check"><input type="checkbox" checked={settings.allRows} onChange={(event) => update({ allRows: event.target.checked })} />{labels.allRows}</label>
            <p>{labels.allRowsHelp}</p>
            {settings.allRows && <label>{labels.tableDensity}<select value={settings.density} onChange={(event) => update({ density: event.target.value })}>
              <option value="original">{labels.original}</option><option value="compact">{labels.compact}</option><option value="normal">{labels.normal}</option><option value="large">{labels.large}</option>
            </select></label>}
            <button type="button" className="datox-react-dashboard__button" onClick={() => { layoutOverridesRef.current = {}; setSettings({ ...defaults }) }}>{labels.reset}</button>
          </fieldset>
          <button type="button" className="datox-react-dashboard__button datox-react-dashboard__button--primary" disabled={saving || (settings.allRows && (loadFailed || missingIDs.length > 0))} onClick={save}>{saving ? labels.exporting : labels.export}</button>
        </aside>
        <section className="datox-react-dashboard__pdf-preview" aria-label={labels.title}>
          {loadFailed ? <div role="alert"><p>{labels.exportFailed}</p><button className="datox-react-dashboard__button" type="button" onClick={() => setLoadFailed(false)}>{labels.retry}</button></div>
            : <iframe ref={iframeRef} title={labels.title} sandbox="allow-scripts allow-same-origin" srcDoc={html} onLoad={bindPreviewInteractions} />}
        </section>
      </div>
    </dialog>
  )
}

function withLayoutOverrides(settings, layouts) {
  const widgets = { ...settings.widgets }
  Object.entries(layouts).forEach(([tabID, tabLayouts]) => {
    widgets[tabID] = { ...widgets[tabID] }
    Object.entries(tabLayouts).forEach(([widgetID, layout]) => {
      widgets[tabID][widgetID] = { ...widgets[tabID][widgetID], layout }
    })
  })
  return { ...settings, widgets }
}
